import React, { useEffect, useState, useContext } from 'react';
import style from './index.module.css';

import { connectStateResults } from 'react-instantsearch-dom';
import { StateResultsProvided } from 'react-instantsearch-core';

import 'rheostat/initialize';
import { AppContext } from '../../app';
import { SiteConfig, TabConfigByName } from '../../config';
import SortBy from '../algolia/sort_by';
import DateListFilter from '../algolia/date_list_filter';
import DateRangeInputFilter from '../algolia/date_range_input';
import PriceRangeSliderFilter from '../algolia/price_range_slider';
import RefinementListFilter from '../algolia/refinement_list_filter';
import { ClearRefinements } from '../algolia/clear_refinements';

import {
    productListFilteredDataEmptyState,
    ProductListFilteredDataProps,
    trackProductListViewed,
    trackProductListFiltered,
    updateProductListFilteredTrackingData,
} from '../../utils/segmentTracking';

type Props = {
    tab: string;
    filters: {
        component: string;
        attribute: string;
        label: string;
        description?: string;
    }[];
};

const FilterComponents: any = {
    DateList: DateListFilter,
    DateRangeInput: DateRangeInputFilter,
    PriceRangeSlider: PriceRangeSliderFilter,
    RefinementList: RefinementListFilter,
};

const Filters = ({ tab, filters }: Props): React.ReactElement => {
    const appContext = useContext(AppContext);
    return (
        <div className={style.container}>
            <h2 className={style.header}>Filters</h2>

            <div className={style.clear}>
                <ClearRefinements />
            </div>

            <div className={style.sortByWrapper}>
                <div className={style.accordionHeader}>Sort By</div>
                <SortBy
                    type="radio"
                    defaultRefinement={SiteConfig.algoliaIndex}
                    items={(() => {
                        const tabConfig = TabConfigByName[appContext.currentTab];
                        return tabConfig?.sorts || [];
                    })()}
                />
            </div>

            {filters.map((filter) => {
                // Use the most common filter as a default just in case
                const FilterComponent = FilterComponents[filter.component] || RefinementListFilter;

                const componentProps: {
                    label: string;
                    attribute: string;
                    description?: string;
                } = {
                    label: filter.label,
                    attribute: filter.attribute,
                };
                // TODO: make descriptions available on all filter types?
                if (filter.component === 'DateList') {
                    componentProps.description = filter.description;
                }

                // This key should be unique and stable enough to suffice
                const componentKey = tab + '_' + filter.label + '_' + filter.attribute;

                return <FilterComponent key={componentKey} {...componentProps} />;
            })}

            <StateResults tab={tab} />
        </div>
    );
};

const StateResults = connectStateResults<StateResultsProvided & { tab: string }>(
    ({ searchState, searchResults, tab }) => {
        const nbHits = searchResults && searchResults.nbHits;
        const [trackingData, setTrackingData] = useState<ProductListFilteredDataProps>(
            productListFilteredDataEmptyState,
        );

        useEffect(() => {
            if (tab && searchResults) {
                trackProductListViewed({
                    searchQuery: searchResults.query,
                    tab: tab,
                });
            }
        }, [tab]);

        useEffect(() => {
            const lastAppliedFilterValue = trackingData.lastAppliedValue;
            updateProductListFilteredTrackingData({
                searchState,
                trackingData,
                setTrackingData,
            });
            if (trackingData.appliedFilters.length > 0 && lastAppliedFilterValue !== trackingData.lastAppliedValue) {
                trackProductListFiltered({
                    appliedFilters: trackingData.appliedFilters,
                    searchQuery: searchResults.query,
                    filterApplied: trackingData.lastAppliedFilter,
                    filterWidgetType: trackingData.filterWidgetType,
                    resultsCount: nbHits,
                    tab: tab,
                });
            }
        }, [searchResults]);

        return null;
    },
);

export default Filters;
